/**
 * ============================================================================
 * RolePermissionPage — 역할×메뉴 권한 매트릭스 관리
 * ============================================================================
 *
 * 【역할】
 *   선택 역할(Role) 에 대해 메뉴별 6종 액션 권한을 Checkbox 매트릭스로 편집.
 *   액션: R(read) / C(create) / U(update) / D(delete) / E(export) / P(print).
 *   RBAC 권한의 핵심 화면 — 역할 단위 일괄 관리.
 *
 * 【주요 기능】
 *   - 역할 선택 Select → 해당 역할의 메뉴 권한 자동 로드
 *   - 메뉴 × 6 체크박스 그리드 (헤더 체크박스로 컬럼 전체 토글)
 *   - 벌크 액션: 전체 읽기 허용 / 전체 쓰기 허용 / 전체 해제 / 현재 행 반전
 *   - 저장 시 변경분(_state='U') 만 서버 전송 (DataSet C/U/D 추적)
 *
 * 【Backend 호출】
 *   - POST /api/v1/transaction (service=adminRole/searchRoleList)       — 역할 목록
 *   - POST /api/v1/transaction (service=adminRole/searchPermissionList) — 매트릭스 조회
 *   - POST /api/v1/transaction (service=adminRole/savePermission)       — 매트릭스 저장
 *
 * 【DataSet/Hook】
 *   - useDataSet<RoleRow>() — roleDs : 역할 목록 (Select 옵션)
 *   - useDataSet<PermRow>() — permDs : 메뉴×액션 매트릭스 (PK: roleId+menuId)
 *   - useTransaction()      — RPC 통합 호출
 *   - useLabel()            — i18n 라벨(t)
 *
 * 【사용처】 router/index.tsx — `{ path: '/admin/permission', element: <RolePermissionPage /> }`
 * 【포팅 원본】 vue-spring-fw `frontend/src/pages/PageRolePermission.vue`
 * 【의존성】 antd Button/Card/Checkbox/Select/Space/Tag, framework hooks
 */
import { useEffect, useMemo, useState } from 'react'
import { Button, Card, Checkbox, Select, Space, Tag } from 'antd'
import CrudToolbar from '@/components/common/CrudToolbar'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'
import { useDataSet, useTransaction } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import { makeColumns, type ColumnSpec } from '@/framework/grid/makeColumns'
import type { TrackedRow } from '@/framework/useDataSet'
import type { ColumnType } from 'antd/es/table'

/** 역할 1행 — Select 옵션 데이터. */
interface RoleRow {
  /** 역할 PK. */
  roleId: string
  /** 역할 표시명. */
  roleName: string
}

/** 메뉴×액션 권한 매트릭스 1행 — 6개 액션을 0/1 bitmap 으로 보관. */
interface PermRow {
  /** 메뉴 PK (FK → fw_menu.menuId). */
  menuId: string
  /** 메뉴 표시명 (서버 join). */
  menuName: string
  /** 조회(R) 권한 (1=허용, 0=차단). */
  canRead: number
  /** 생성(C) 권한. */
  canCreate: number
  /** 수정(U) 권한. */
  canUpdate: number
  /** 삭제(D) 권한. */
  canDelete: number
  /** Excel Export(E) 권한. */
  canExport: number
  /** 인쇄(P) 권한. */
  canPrint: number
}

/** 0/1/'Y'/true 등 다양한 truthy 표현을 boolean 으로 정규화. */
const isOn = (v: unknown) => v === 1 || v === '1' || v === true || v === 'Y'

const FLAGS: { key: keyof PermRow; labelKey: string; fallback: string }[] = [
  { key: 'canRead', labelKey: 'COL_CAN_READ', fallback: '조회' },
  { key: 'canCreate', labelKey: 'COL_CAN_CREATE', fallback: '생성' },
  { key: 'canUpdate', labelKey: 'COL_CAN_UPDATE', fallback: '수정' },
  { key: 'canDelete', labelKey: 'COL_CAN_DELETE', fallback: '삭제' },
  { key: 'canExport', labelKey: 'COL_CAN_EXPORT', fallback: '엑셀' },
  { key: 'canPrint', labelKey: 'COL_CAN_PRINT', fallback: '출력' }
]

/**
 * RolePermissionPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function RolePermissionPage() {
  const roleDs = useDataSet<RoleRow>()
  const permDs = useDataSet<PermRow>()
  const { transaction } = useTransaction()
  const { t } = useLabel()
  const [roleId, setRoleId] = useState<string>('')
  const [selectedPermIdx, setSelectedPermIdx] = useState<number>(-1)

  /** 표시 중 모든 행에 동일 patch 적용 (벌크 토글). */
  const bulkSet = (patch: Partial<PermRow>) => {
    permDs.getVisibleRows().forEach((_r, i) => {
      permDs.updateRow(i, patch)
    })
  }
  /** [전체 읽기 허용] — 모든 메뉴 canRead=1. */
  const bulkAllRead = () => bulkSet({ canRead: 1 })
  /** [전체 쓰기 허용] — 모든 메뉴 canCreate/canUpdate/canDelete=1. */
  const bulkAllWrite = () => bulkSet({ canCreate: 1, canUpdate: 1, canDelete: 1 })
  /** [전체 해제] — 모든 메뉴 6개 액션 0. */
  const bulkClear = () =>
    bulkSet({
      canRead: 0,
      canCreate: 0,
      canUpdate: 0,
      canDelete: 0,
      canExport: 0,
      canPrint: 0
    })
  /** [현재 행 반전] — 선택 행의 6개 액션을 모두 0↔1 토글. */
  const invertCurrentRow = () => {
    if (selectedPermIdx < 0) return
    const row = permDs.getVisibleRows()[selectedPermIdx]
    if (!row) return
    const flip = (v: unknown) => (isOn(v) ? 0 : 1)
    permDs.updateRow(selectedPermIdx, {
      canRead: flip(row.data.canRead),
      canCreate: flip(row.data.canCreate),
      canUpdate: flip(row.data.canUpdate),
      canDelete: flip(row.data.canDelete),
      canExport: flip(row.data.canExport),
      canPrint: flip(row.data.canPrint)
    })
  }

  /** 역할 목록 조회 — Select 옵션용 (페이지 진입 시 1회). */
  const searchRoles = () =>
    transaction({
      id: 'role_search',
      service: 'adminRole/searchRoleList',
      action: 'search',
      outds: { ds_role: roleDs }
    })

  /**
   * 선택 역할의 메뉴 권한 매트릭스 조회.
   * @param rid 외부 roleId (생략 시 state)
   * 미선택 시 무동작.
   */
  const searchPerms = (rid?: string) => {
    const id = rid ?? roleId
    if (!id) return
    return transaction({
      id: 'permission_search',
      service: 'adminRole/searchPermissionList',
      action: 'search',
      inds: { ds_search: { roleId: id } },
      outds: { ds_permission: permDs }
    })
  }

  /**
   * 권한 매트릭스 저장 — adminRole/savePermission.
   * - ds_search 로 roleId 컨텍스트 전달, ds_permission 으로 변경분 전송
   * - 성공 시 매트릭스 재조회로 동기화
   */
  const save = async () => {
    if (!roleId) return
    const res = await transaction({
      id: 'permission_save',
      service: 'adminRole/savePermission',
      action: 'save',
      inds: { ds_search: { roleId }, ds_permission: permDs }
    })
    if (res.success) searchPerms()
  }

  useEffect(() => {
    searchRoles()
  }, [])

  /** 역할 Select 변경 — state 갱신 + 매트릭스 자동 재조회. */
  const onRoleChange = (v: string) => {
    setRoleId(v)
    searchPerms(v)
  }

  /** 단일 셀 체크박스 토글 — boolean → 0/1 변환 후 updateRow. */
  const toggleCell = (idx: number, field: keyof PermRow, checked: boolean) => {
    permDs.updateRow(idx, { [field]: checked ? 1 : 0 } as Partial<PermRow>)
  }

  /** 컬럼 헤더 체크박스 토글 — 표시 중 모든 행의 동일 컬럼 일괄 set. */
  const toggleAll = (field: keyof PermRow, checked: boolean) => {
    permDs.getVisibleRows().forEach((r, i) => {
      permDs.updateRow(i, { [field]: checked ? 1 : 0 } as Partial<PermRow>)
    })
  }

  /** 헤더 체크박스 'checked' 상태 — 모든 행이 ON 인지 판정. */
  const allChecked = (field: keyof PermRow) => {
    const rows = permDs.getVisibleRows()
    return rows.length > 0 && rows.every((r) => isOn(r.data[field]))
  }
  /** 헤더 체크박스 'indeterminate' 상태 — 일부만 ON 인지 판정. */
  const someChecked = (field: keyof PermRow) => {
    const rows = permDs.getVisibleRows()
    const yes = rows.filter((r) => isOn(r.data[field])).length
    return yes > 0 && yes < rows.length
  }

  const columns = useMemo(() => {
    type PermTRow = TrackedRow<PermRow>
    const baseSpecs: ColumnSpec<PermTRow>[] = [
      { key: 'COL_MENU_ID', dataIndex: ['data', 'menuId'], width: 180 },
      { key: 'COL_MENU_NAME', dataIndex: ['data', 'menuName'], width: 240 }
    ]
    const base = makeColumns(baseSpecs, t)
    const flagCols: ColumnType<PermTRow>[] = FLAGS.map((f) => ({
      key: f.labelKey,
      title: (
        <Checkbox
          checked={allChecked(f.key)}
          indeterminate={someChecked(f.key)}
          onChange={(e) => toggleAll(f.key, e.target.checked)}
        >
          {t(f.labelKey, f.fallback)}
        </Checkbox>
      ),
      width: 100,
      align: 'center',
      render: (_v: unknown, r: PermTRow, i: number) => (
        <Checkbox
          checked={isOn(r.data[f.key])}
          onChange={(e) => toggleCell(i, f.key, e.target.checked)}
        />
      )
    }))
    const statusCol: ColumnType<PermTRow> = {
      title: t('COL_STATUS', '상태'),
      dataIndex: '_state',
      width: 70,
      render: (s: unknown) => (s === 'U' ? <Tag color="orange">{t('TTL_EDIT', '수정')}</Tag> : null)
    }
    return [...base, ...flagCols, statusCol]
  }, [t, permDs.rows, selectedPermIdx])

  return (
    <PageContainer>
      <Card size="small">
        <Space>
          <span>{t('LBL_ROLE_COLON', '역할:')}</span>
          <Select
            style={{ width: 240 }}
            value={roleId || undefined}
            placeholder={t('PH_ROLE_SELECT', '역할 선택')}
            onChange={onRoleChange}
            options={roleDs.rows.map((r) => ({
              label: `${r.data.roleName} (${r.data.roleId})`,
              value: r.data.roleId
            }))}
          />
        </Space>
      </Card>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: 8,
          overflowX: 'auto'
        }}
      >
        <Space size="small" wrap={false} style={{ flexShrink: 0 }}>
          <Button onClick={bulkAllRead}>{t('BTN_ALL_READ_ALLOW', '전체 읽기 허용')}</Button>
          <Button onClick={bulkAllWrite}>{t('BTN_ALL_WRITE_ALLOW', '전체 쓰기 허용')}</Button>
          <Button onClick={bulkClear}>{t('BTN_ALL_CLEAR', '전체 해제')}</Button>
          <Button onClick={invertCurrentRow} disabled={selectedPermIdx < 0}>
            {t('BTN_INVERT_CURRENT', '현재 행 반전')}
          </Button>
        </Space>
        <CrudToolbar
          onSearch={() => searchPerms()}
          onSave={save}
          dirty={permDs.isDirty}
          style={{ width: 'auto', flexShrink: 0 }}
        />
      </div>
      <DataTable<TrackedRow<PermRow>>
        rowKey="_rowId"
        dataSource={permDs.getVisibleRows()}
        columns={columns}
        pagination={false}
        rowClassName={(_r, i) => (i === selectedPermIdx ? 'ant-table-row-selected' : '')}
        onRow={(_r, i) => ({ onClick: () => setSelectedPermIdx(i ?? -1) })}
      />
    </PageContainer>
  )
}
